import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'

import {
  getCheapestPurchasableOption,
  getIsTourOfferPurchasable,
  getSortedItinerary,
} from 'selectors/TourV2/tourV2OfferPageSelectors'
import { OfferPageState, TourV2MonthFilter } from 'contexts/OfferPage/offerPageStateReducer'
import { getShareModalConfig, ShareModalConfig } from 'lib/customer/getShareModalConfig'
import {
  TOUR_V2_CONTENT_SECTION_LABELS,
  TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS,
  TourV2ContentSections,
} from 'constants/tours'
import delay from 'lib/utils/delay'
import determineTourV2OfferContentSections from 'lib/tours/determineTourV2OfferContentSections'
import Heading from 'components/Luxkit/Typography/Heading'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import Subtitle from 'components/Luxkit/Typography/Subtitle'
import useHasGotToAnchor from 'hooks/useHasGotToAnchor'
import useOnScreen from 'hooks/useOnScreen'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'

import TourV2Departures from './Departures/TourV2Departures'
import TourV2FinePrint from './FinePrint/TourV2FinePrint'
import TourV2HeadData from './TourV2HeadData'
import TourV2Inclusions from './Inclusions/TourV2Inclusions'
import TourV2ItineraryAndMap from './Itinerary/TourV2ItineraryAndMap'
import TourV2Variations from './TourVariations/TourV2Variations'
import TourV2Overview from './TourV2Overview'
import TourV2StickyHeader from './StickyElements/TourV2StickyHeader'
import TourV2FAQ from './FAQ/TourV2FAQ'
import TourV2BookWithConfidence from './BookWithConfidence/TourV2BookWithConfidence'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import TourV2UnavailabilityCard from './TourV2UnavailabilityCard'
import TextButton from 'components/Luxkit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import { getOfferPalette } from 'lib/offer/offerUtils'
import TourV2YouMayAlsoLikeCarrousel from './YouMayAlsoLike/TourV2YouMayAlsoLikeCarrousel'
import UserReviewList from '../NavigableOfferContent/UserReviewList'
import { useSaveHotLead } from 'hooks/Recommendation/useSaveHotLead'
import * as Analytics from 'analytics/analytics'
import useTourV2Details from 'hooks/TourV2/useTourV2Details'
import OfferPageGallery from '../OfferPageGallery'
import { offerPageHotLeadEvent } from 'analytics/snowplow/events'
import TourV2StickyFooter from './StickyElements/TourV2StickyFooter'
import usePrevious from 'hooks/usePrevious'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import Group from 'components/utils/Group'
import LuxPlusNotificationBanner from 'luxPlus/components/Banners/LuxPlusNotificationBanner'
import { isLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import AdditionalWaysToExperienceSection from './AdditionalWaysToExperience/AdditionalWaysToExperienceSection'
import SavePromptTour from 'tripPlanner/components/Bookmark/SavePrompt/SavePromptTour'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useInView } from 'react-intersection-observer'
import { objectKeys } from 'lib/object/objectUtils'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import useMountedEffect from 'hooks/useMountedEffect'

const UnavailabilitySectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};
`

const SectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(40)};
  padding-top: ${rem(40)};

  > .section-title + .section-subtitle {
    margin-top: ${rem(8)}
  }

  > .section-title + .section-content,
  > .section-subtitle + .section-content {
    margin-top: ${rem(32)};
  }
`

const OverviewSectionContainer = styled(LayoutContainer)`
  padding-bottom: ${rem(24)};
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-bottom: ${rem(64)};
  }
`

// We're using a 8px high, full width 'divider'. There is no lux kit
// equiv at the moment, so fake it with a small pane
const SectionDivider = styled(Pane)`
  height: ${rem(8)};
`

interface MappedProps {
  cheapestPurchasableOption?: App.Tours.TourV2OfferPurchasableOption
  isOfferPurchasable: boolean
  itinerary: Array<App.Tours.TourV2OfferItineraryItem>
  offer: App.Tours.TourV2Offer
  rooms?: OfferPageState['rooms']
  departureMonth?: TourV2MonthFilter;
  variation: App.Tours.TourV2OfferVariation
}

interface Props {
  shareModalConfig?: ShareModalConfig
}

interface SectionAnchor {
  end: HTMLDivElement | null
  start: HTMLDivElement | null
}

function TourV2Content(props: Props & MappedProps) {
  const {
    cheapestPurchasableOption,
    isOfferPurchasable,
    itinerary,
    offer,
    rooms,
    variation,
    departureMonth,
    shareModalConfig,
  } = props

  const { finePrint, faq } = offer

  const [activeSection, setActiveSection] = useState<TourV2ContentSections | undefined>()
  const [scrollTarget, setScrollTarget] = useState<TourV2ContentSections | null>(null)

  const luxPlusEnabled = useAppSelector(isLuxPlusEnabled)
  const region = useAppSelector((state) => state.geo.currentRegionCode)

  const galleryAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const overviewAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const variationsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const itineraryAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const inclusionsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const departuresDatesAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const additionalWaysAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const finePrintAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const faqAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const reviewsAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const footerAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const monthCarouselAnchor = useRef<SectionAnchor>({ start: null, end: null })
  const [monthCarouselRef, monthCarouselInView] = useInView({ rootMargin: '-120px 0px 0px 0px' })

  const sectionsVisible: Record<TourV2ContentSections, boolean> = {
    [TourV2ContentSections.GALLERY]: useOnScreen(galleryAnchor.current?.start, galleryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.OVERVIEW]: useOnScreen(overviewAnchor.current?.start, overviewAnchor.current?.end, -120, 120),
    [TourV2ContentSections.VARIATIONS]: useOnScreen(variationsAnchor.current?.start, variationsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.ITINERARY]: useOnScreen(itineraryAnchor.current?.start, itineraryAnchor.current?.end, -120, 120),
    [TourV2ContentSections.DEPARTURES]: useOnScreen(departuresDatesAnchor.current?.start, departuresDatesAnchor.current?.end, -120, 120),
    [TourV2ContentSections.ADDITIONAL_WAYS]: useOnScreen(additionalWaysAnchor.current?.start, additionalWaysAnchor.current?.end, -120, 120),
    [TourV2ContentSections.INCLUSIONS]: useOnScreen(inclusionsAnchor.current?.start, inclusionsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FINE_PRINT]: useOnScreen(finePrintAnchor.current?.start, finePrintAnchor.current?.end, -120, 120),
    [TourV2ContentSections.FAQ]: useOnScreen(faqAnchor.current?.start, faqAnchor.current?.end),
    [TourV2ContentSections.REVIEWS]: useOnScreen(reviewsAnchor.current?.start, reviewsAnchor.current?.end, -120, 120),
    [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: false,
    [TourV2ContentSections.DEPARTURE_DATES]: false,
  }

  const hasGotToOverview = useHasGotToAnchor(overviewAnchor.current?.start, -110)
  const hasGotToDepartures = useHasGotToAnchor(departuresDatesAnchor.current?.start, -136)

  const scrollToAnchor = useCallback(
    (section: TourV2ContentSections) => {
      const sectionAnchorsRefs: Record<TourV2ContentSections, React.MutableRefObject<SectionAnchor> | undefined> = {
        [TourV2ContentSections.GALLERY]: galleryAnchor,
        [TourV2ContentSections.OVERVIEW]: overviewAnchor,
        [TourV2ContentSections.VARIATIONS]: variationsAnchor,
        [TourV2ContentSections.ITINERARY]: itineraryAnchor,
        [TourV2ContentSections.DEPARTURES]: departuresDatesAnchor,
        [TourV2ContentSections.ADDITIONAL_WAYS]: additionalWaysAnchor,
        [TourV2ContentSections.INCLUSIONS]: inclusionsAnchor,
        [TourV2ContentSections.FINE_PRINT]: finePrintAnchor,
        [TourV2ContentSections.FAQ]: faqAnchor,
        [TourV2ContentSections.REVIEWS]: reviewsAnchor,
        [TourV2ContentSections.YOU_MAY_ALSO_LIKE]: undefined,
        [TourV2ContentSections.DEPARTURE_DATES]: monthCarouselAnchor,
      }

      if (section !== TourV2ContentSections.DEPARTURE_DATES) {
        // departure dates isn't a real section, we don't scroll target it
        setScrollTarget(section)
      }
      sectionAnchorsRefs[section]!.current?.start?.scrollIntoView()
    },
    [setScrollTarget],
  )

  const contentSections = useMemo(() =>
    determineTourV2OfferContentSections(offer, region, variation)
  , [offer, variation, region])

  const headerContentSections = useMemo(() => [...contentSections].filter(section => TOUR_V2_STICKY_HEADER_ENABLED_SECTIONS.has(section)),
    [contentSections])

  const currentOnScreenSection = objectKeys(sectionsVisible).find((section) => sectionsVisible[section] && (!scrollTarget || section === scrollTarget)) as TourV2ContentSections

  const acknowledgeVariarionChange = useCallback((variationId: string) => {
    const viewingVariation = offer.variations[variationId]
    delay(500).then(() => showSnackbar(
      `You're now viewing ${viewingVariation.name}`,
      'success',
    ))
  }, [offer])

  useEffect(
    () => {
      const hasScrolledToTargetSection = !scrollTarget || currentOnScreenSection === scrollTarget
      if (hasScrolledToTargetSection) {
        setActiveSection(currentOnScreenSection)
        setScrollTarget(null)
      }
    },
    [scrollTarget, currentOnScreenSection],
  )

  const paletteValue = getOfferPalette(offer, luxPlusEnabled)
  const { hasAlternativeRoute } = useTourV2Details(offer)

  const previousSelectedDepartureMonth = usePrevious(departureMonth)
  const expressedDateInterest = (departureMonth != previousSelectedDepartureMonth) && (previousSelectedDepartureMonth != undefined)

  useSaveHotLead({
    offerId: offer.id,
    offerType: offer.type,
    occupancy: rooms,
    variant: Object.keys(offer.variations).length > 1 ? variation?.fkTourOptionId : undefined,
    year: expressedDateInterest && departureMonth ? departureMonth.year : undefined,
    month: expressedDateInterest && departureMonth ? departureMonth.month + 1 : undefined,
  })
  const offerPageState = useContext(OfferPageStateContext)
  useMountedEffect(() => {
    if (offerPageState.rooms && offerPageState.departureMonth) {
      Analytics.trackEvent(offerPageHotLeadEvent(offer, offerPageState))
    }
  }, [offerPageState.rooms, offerPageState.departureMonth])

  const alternativeVariation = useMemo(() => {
    if (hasAlternativeRoute) {
      const alternativeVariationId = Object.keys(offer.variations).filter((variationId) => variationId !== variation?.id)[0]
      return offer.variations[alternativeVariationId]
    }
  }, [variation, hasAlternativeRoute, offer.variations])

  // we only recommend tours with time filter when user specifically select a departure time rather than using the default departure time
  const defaultDeparture = useRef('')
  const [recommendDepartureYear, recommendDepartureMonth] = useMemo(() => {
    if (departureMonth) {
      const selectedDeparture = `${departureMonth.year}-${departureMonth.month}`
      if (!defaultDeparture.current) {
        defaultDeparture.current = selectedDeparture
      }
      if (selectedDeparture !== defaultDeparture.current) {
        return [departureMonth.year, departureMonth.month]
      }
    }
    return [undefined, undefined]
  }, [departureMonth])

  const shareConfig = shareModalConfig ? getShareModalConfig(shareModalConfig) : undefined

  return (<ProductPaletteProvider product={offer}>
    <SavePromptTour offer={offer} selectedVariation={variation} />

    <TourV2HeadData offer={offer} variation={variation} />

    <TourV2StickyHeader
      contentSections={headerContentSections}
      onScrollTo={scrollToAnchor}
      purchasableOption={cheapestPurchasableOption}
      visible={hasGotToOverview}
      title={variation?.name}
      activeSection={activeSection}
      buttonPaletteValue={paletteValue}
      monthCarouselInView={monthCarouselInView}
    />

    {luxPlusEnabled && !!offer.luxPlus?.access &&
      <LuxPlusNotificationBanner>
        {offer.luxPlus.access === 'earlyAccess' && 'Members get early access to this offer before the general public and more'}
        {offer.luxPlus.access === 'memberOnly' && 'You get exclusive access to this member-only offer'}
      </LuxPlusNotificationBanner>
    }

    <div id="TOUR_V2_CONTENT_TOP_ANCHOR" aria-hidden="true" />

    {!isOfferPurchasable && !!variation && <Pane>
      <UnavailabilitySectionContainer>
        <Group direction="vertical" gap={40}>
          <TourV2UnavailabilityCard offer={offer} offerVariation={variation} />
          <Group direction="vertical" gap={8} horizontalAlign="center">
            <Heading variant="heading3" colour="neutral-one">More offers available</Heading>
            <TextButton kind="secondary" to="/tours">Browse all tour offers</TextButton>
          </Group>
        </Group>
      </UnavailabilitySectionContainer>
    </Pane>}

    {contentSections.has(TourV2ContentSections.GALLERY) && <>
      <ScrollAnchor ref={node => { galleryAnchor.current.start = node }} />
      <OfferPageGallery
        offer={offer}
        tourVariation={variation}
        shareModalConfig={shareConfig}
        autoPlayVideo
      />
      <ScrollAnchor ref={node => { galleryAnchor.current.end = node }} />
    </>}
    <ProductPaletteProvider palette={paletteValue}>
      <LuxPlusLabelProvider page="offer-page">
        <Pane>
          <OverviewSectionContainer>
            <ScrollAnchor ref={node => { overviewAnchor.current.start = node }} />
            <TourV2Overview
              cheapestPurchasableOption={cheapestPurchasableOption}
              shareModalConfig={shareModalConfig}
              onView={scrollToAnchor}
              buttonPaletteValue={paletteValue}
            />
            <ScrollAnchor ref={node => { overviewAnchor.current.end = node }} />
          </OverviewSectionContainer>

          {contentSections.has(TourV2ContentSections.VARIATIONS) && <SectionContainer size="lg">
            <ScrollAnchor ref={node => { variationsAnchor.current.start = node }} />
            <Heading variant="heading3" align="start" className="section-title">Choose a tour option</Heading>
            <div className="section-content">
              <TourV2Variations onVariationChanged={acknowledgeVariarionChange} />
            </div>
            <ScrollAnchor ref={node => { variationsAnchor.current.end = node }} id="TOUR_V2_ITINERARY_TOP_ANCHOR"/>
          </SectionContainer>}
          {contentSections.has(TourV2ContentSections.ITINERARY) && <SectionContainer
            size={!variation?.routeMapImage ? 'md' : undefined}
          >
            <ScrollAnchor ref={node => { itineraryAnchor.current.start = node }} />
            <Heading variant="heading2" align="center" className="section-title">
              {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.ITINERARY]}{variation?.routeMapImage && ' & map'}
            </Heading>
            <div className="section-content">
              <TourV2ItineraryAndMap
                itinerary={itinerary}
                variation={variation}
                offerType={offer.type}
                alternativeVariation={alternativeVariation}
                onAlternativeRouteChange={acknowledgeVariarionChange}
              />
            </div>
            <ScrollAnchor ref={node => { itineraryAnchor.current.end = node }} />
          </SectionContainer>}

          {contentSections.has(TourV2ContentSections.INCLUSIONS) && <SectionContainer size="md">
            <ScrollAnchor ref={node => { inclusionsAnchor.current.start = node }} />
            <Heading variant="heading2" align="center" className="section-title">
              {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.INCLUSIONS]}
            </Heading>
            <Subtitle variant="subtitle2" align="center" className="section-subtitle">
              The following are included in your tour package:
            </Subtitle>
            <div className="section-content">
              <TourV2Inclusions
                diningInclusions={variation?.diningInclusions}
                travelInclusions={variation?.travelInclusions}
              />
            </div>
            <ScrollAnchor ref={node => { inclusionsAnchor.current.end = node }} />
          </SectionContainer>}
        </Pane>
        <SectionDivider type="light" />
        <Pane>
          <VerticalSpacer gap={32}>
            <ScrollAnchor ref={node => { departuresDatesAnchor.current.start = node }} />
            <LayoutContainer size="lg">
              <Heading variant="heading2" align="center" className="section-title">
                {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.DEPARTURES]}
              </Heading>
            </LayoutContainer>
            <TourV2Departures
              monthCarouselRef={node => {
                monthCarouselAnchor.current.start = node
                monthCarouselRef(node)
              }}
            />
            <ScrollAnchor ref={node => { departuresDatesAnchor.current.end = node }} />
          </VerticalSpacer>
        </Pane>
        <SectionDivider type="light" />
      </LuxPlusLabelProvider>

      <Pane>
        {contentSections.has(TourV2ContentSections.ADDITIONAL_WAYS) && <SectionContainer size="md">
          <ScrollAnchor ref={node => { additionalWaysAnchor.current.start = node }} />
          <Heading variant="heading3" align="start" className="section-title">
            {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.ADDITIONAL_WAYS]}
          </Heading>
          <div className="section-content">
            <AdditionalWaysToExperienceSection />
          </div>
          <ScrollAnchor ref={node => { additionalWaysAnchor.current.end = node }} />
        </SectionContainer>}

        <SectionContainer size="md">
          <ScrollAnchor ref={node => { finePrintAnchor.current.start = node }} />
          <Heading variant="heading2" align="center" className="section-title">
            {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FINE_PRINT]}
          </Heading>
          <div className="section-content">
            <TourV2FinePrint finePrint={finePrint} />
          </div>
          <ScrollAnchor ref={node => { finePrintAnchor.current.end = node }} />
        </SectionContainer>

        {contentSections.has(TourV2ContentSections.REVIEWS) && (
          <SectionContainer size="md">
            <ScrollAnchor ref={node => { reviewsAnchor.current.start = node }} />
            <div className="section-content">
              <UserReviewList tour={offer} />
            </div>
            <ScrollAnchor ref={node => { reviewsAnchor.current.end = node }} />
          </SectionContainer>
        )}

        {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
          <ScrollAnchor ref={node => { faqAnchor.current.start = node }} />
          <Heading variant="heading2" align="center" className="section-title">
            {TOUR_V2_CONTENT_SECTION_LABELS[TourV2ContentSections.FAQ]}
          </Heading>
          <div className="section-content">
            <TourV2FAQ faq={faq} />
          </div>
          <ScrollAnchor ref={node => { faqAnchor.current.end = node }} />
        </SectionContainer>}

        {contentSections.has(TourV2ContentSections.YOU_MAY_ALSO_LIKE) && <SectionContainer as="div" >
          <div className="section-content">
            <TourV2YouMayAlsoLikeCarrousel
              offerId={offer.id}
              trackingDescription="Tours"
              departureYear={recommendDepartureYear}
              departureMonth={recommendDepartureMonth}
            />
          </div>
        </SectionContainer>}

      </Pane>
      <ScrollAnchor ref={node => { footerAnchor.current.start = node }} />
      {isOfferPurchasable && <TourV2StickyFooter
        visible={!hasGotToDepartures}
        purchasableOption={cheapestPurchasableOption}
        onScrollTo={scrollToAnchor}
      />}

      {contentSections.has(TourV2ContentSections.FAQ) && <SectionContainer size="md">
        <div className="section-content">
          <TourV2BookWithConfidence />
        </div>
      </SectionContainer>}
    </ProductPaletteProvider>
  </ProductPaletteProvider>)
}

function mapOfferPageStateToProps(offerPageState: OfferPageState, appState: App.State): MappedProps {
  return {
    cheapestPurchasableOption: getCheapestPurchasableOption(offerPageState, appState),
    itinerary: getSortedItinerary(offerPageState),
    offer: offerPageState.tourV2Offer!,
    rooms: offerPageState.rooms,
    departureMonth: offerPageState.departureMonth,
    variation: offerPageState.tourV2Variation!,
    isOfferPurchasable: getIsTourOfferPurchasable(offerPageState),
  }
}

export default withOfferPageAndReduxState(mapOfferPageStateToProps)(TourV2Content)
